import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';
import getBaseURL from '@utils/api/getBaseURL';

import type { AgencyFormMailRequestViewModel } from '../models/AgencyFormMailRequestViewModel';
import type { ApplicationFormMailRequestViewModel } from '../models/ApplicationFormMailRequestViewModel';
import type { C4CFormRequestViewModel } from '../models/C4CFormRequestViewModel';
import type { ComboViewModel } from '../models/ComboViewModel';
import type { CompliantFormMailRequestViewModel } from '../models/CompliantFormMailRequestViewModel';
import type { ContactFormMailRequestViewModel } from '../models/ContactFormMailRequestViewModel';
import type { FormsKeyValueViewModel } from '../models/FormsKeyValueViewModel';
import type { SetairCompliantFormMailRequestViewModel } from '../models/SetairCompliantFormMailRequestViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class MailsService {
  /**
   * Send Mail (Contact Form)
   * Sends mail for contact form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailContactForm(
    {
      body,
    }: {
      body?: ContactFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/contact`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Application Form)
   * Sends mail for application form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailApplicationForm(
    {
      body,
    }: {
      body?: ApplicationFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/application`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Compliant Form)
   * Sends mail for compliant form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailCompliantForm(
    {
      body,
    }: {
      body?: CompliantFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/compliant`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Setair Compliant)
   * Sends mail for compliant form to setair
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailCompliantFormSetair(
    {
      body,
    }: {
      body?: SetairCompliantFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/setair`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Agency)
   * Sends mail for agency form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailAgencyForm(
    {
      body,
    }: {
      body?: AgencyFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/agency`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Subjects (Compliant Form)
   * Gets subjects for compliant form
   * @returns ComboViewModel Success
   * @throws ApiError
   */
  public static async getSubjects(
    {
      type,
    }: {
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ComboViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<ComboViewModel>>({
      method: 'GET',
      url: getBaseURL(`/v2/mails/subjects`, `MailsService`),
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Forms
   * Gets forms for C4C
   * @returns FormsKeyValueViewModel Success
   * @throws ApiError
   */
  public static async getForms(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<FormsKeyValueViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<FormsKeyValueViewModel>>({
      method: 'GET',
      url: getBaseURL(`/v2/mails/forms`, `MailsService`),
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Forms
   * Sends forms to C4C
   * @returns string Success
   * @throws ApiError
   */
  public static async sendForms(
    {
      body,
    }: {
      body?: C4CFormRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: getBaseURL(`/v2/mails/forms`, `MailsService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Offices (Compliant Form)
   * Gets offices for compliant form
   * @returns ComboViewModel Success
   * @throws ApiError
   */
  public static async getOffices(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ComboViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<ComboViewModel>>({
      method: 'GET',
      url: getBaseURL(`/v2/mails/offices`, `MailsService`),
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get agency types (Agency Form)
   * Gets agency types for agency form
   * @returns ComboViewModel Success
   * @throws ApiError
   */
  public static async getAgencyTypes(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ComboViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<ComboViewModel>>({
      method: 'GET',
      url: getBaseURL(`/v2/mails/agencytypes`, `MailsService`),
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
